"use client";
import axios from "axios";
import Linkify from "linkify-react";
import { useSession } from "next-auth/react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { BiLogoInstagram, BiLogoLinkedin } from "react-icons/bi";
import { FaEdit } from "react-icons/fa";
import { TbCameraPlus } from "react-icons/tb";
import { toast } from "react-toastify";
import Badge from "~/components/Badge";
import Chip from "~/components/Chip";
import { X } from "~/components/icons/Social";
import PhoneNumberModal from "~/components/profile-components/PhoneNumberModal";
import { formatPhoneNumber } from "~/components/themed/PhoneNumberInput";
import { TertiaryButton } from "~/components/themed/customButtons";
import { H1, H3 } from "~/components/themed/font";
import Spinner from "~/components/themed/spinner";
import { Button } from "~/components/v3/themed/Button";
import { env } from "~/env.mjs";
import { useUserStore } from "~/state/store";
import { RouterOutputs, api } from "~/utils/api";
import { cn } from "~/utils/fontsConfig";
import EditProfileModal from "./EditProfileModal";

type ProfileCardProps = {
    user: RouterOutputs["user"]["getProfile"];
    refetch?: () => void;
};

export const SocialLinksComponent = ({
    socialLinks,
    className,
}: {
    socialLinks?: ProfileCardProps["user"]["socialLinks"];
    className?: string;
}) => {
    return (
        <div className="flex items-center gap-3 ">
            {socialLinks?.x && (
                <a
                    href={socialLinks.x}
                    rel="nofollow"
                    className="cursor-pointer"
                    target="_blank"
                >
                    <X className={cn("h-7 w-7", className)} />
                </a>
            )}
            {socialLinks?.li && (
                <a
                    href={socialLinks.li}
                    rel="nofollow"
                    className="cursor-pointer"
                    target="_blank"
                >
                    <BiLogoLinkedin
                        className={cn("h-7 w-7 text-foreground", className)}
                        aria-hidden="true"
                    />
                </a>
            )}
            {socialLinks?.ig && (
                <a
                    href={socialLinks.ig}
                    rel="nofollow"
                    className="cursor-pointer"
                    target="_blank"
                >
                    <BiLogoInstagram
                        className={cn("h-7 w-7 text-foreground", className)}
                        aria-hidden="true"
                    />
                </a>
            )}
        </div>
    );
};

const ProfileCard = ({ user, refetch }: ProfileCardProps) => {
    const { data: session } = useSession();
    const apiCtx = api.useUtils();
    const router = useRouter();

    const isCurrentUser = session?.user.id === user.id;
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [showEditProfileModal, setShowEditProfileModal] = useState(false);

    const { storeUser, setStoreUser } = useUserStore((state) => ({
        storeUser: state.user,
        setStoreUser: state.setUser,
    }));

    const { data: subscription, refetch: refetchSubscription } =
        api.user.getActiveSubscription.useQuery(undefined, {
            enabled: isCurrentUser,
        });

    const { mutateAsync: fetchPresignedUrls } =
        api.s3.getProfilePresignedUrl.useMutation();
    const updateMutation = api.user.update.useMutation();

    const cancelSubscription = api.user.cancelSubscription.useMutation();

    useEffect(() => {
        if (isCurrentUser && user) {
            setStoreUser(user);
        }

        return () => {};
    }, [user]);

    const handleCancelSubscription = (subscriptionId: string) => {
        cancelSubscription.mutateAsync(
            { id: subscriptionId },
            { onSuccess: () => refetchSubscription() },
        );
    };

    const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        toast.loading("Uploading profile picture", {
            toastId: "upload-profile-picture",
        });

        if (!e.target.files || !e.target.files[0] || !user) return;
        setIsLoading(true);
        const file = e.target.files[0];
        const ext = file.name.substring(file.name.lastIndexOf("."));
        const uploadFilename = `${(Math.random() + 1)
            .toString(36)
            .substring(7)}${ext}`;

        const presignedURL = await fetchPresignedUrls({
            filename: uploadFilename,
        });

        let newFile = new File([file], uploadFilename, {
            type: file.type,
        });

        const resp = await axios
            .put(presignedURL, newFile.slice(), {
                headers: { "Content-Type": newFile.type },
            })
            .catch((err) => console.error(err));

        updateMutation.mutateAsync(
            {
                userId: user.id,
                image: `${env.NEXT_PUBLIC_ASSETS_URL}/profile/${user.id}/${uploadFilename}`,
            },
            {
                onSuccess: () => {
                    refetch && refetch();
                    toast.dismiss("upload-profile-picture");
                    toast.success("Profile picture updated!");
                },
            },
        );
        setIsLoading(false);
    };

    const [showPhoneNumberModal, setShowPhoneNumberModal] = useState(false);

    if (!user) return <Spinner />;
    else
        return (
            <>
                <div className="flex h-full w-full flex-grow flex-col p-8 md:max-w-[1280px] md:flex-row">
                    <div className="mb-4 flex w-full flex-col items-center justify-start md:w-1/4">
                        <div className="relative">
                            {user.image && user.name && (
                                <Image
                                    className={cn(
                                        "h-[175px] rounded-full object-cover",
                                    )}
                                    src={user.image}
                                    alt={user.name}
                                    width={175}
                                    height={175}
                                />
                            )}
                            {isCurrentUser && (
                                <div
                                    className={`absolute inset-x-1/2 
                                                inset-y-1/2 flex h-14 w-14 -translate-x-1/2 
                                                -translate-y-1/2 items-center justify-center 
                                                rounded-full bg-foreground/40 backdrop-blur-sm
                                                `}
                                    onClick={() =>
                                        fileInputRef.current?.click()
                                    }
                                >
                                    {isLoading ? (
                                        <Spinner />
                                    ) : (
                                        <TbCameraPlus
                                            className="h-6 w-6 text-foreground"
                                            aria-hidden="true"
                                        />
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        onChange={handleFileChange}
                                        accept="image/png, image/gif, image/jpeg, image/webp"
                                        hidden
                                    />
                                </div>
                            )}
                        </div>
                        {isCurrentUser && (
                            <TertiaryButton
                                onClick={() => {
                                    setShowEditProfileModal(true);
                                }}
                            >
                                Edit Profile
                            </TertiaryButton>
                        )}
                        {subscription && (
                            <div className="mt-4">
                                Subscription: {subscription.name}
                                <Button
                                    variant="link"
                                    onClick={() =>
                                        handleCancelSubscription(
                                            subscription.id,
                                        )
                                    }
                                >
                                    Cancel
                                </Button>
                            </div>
                        )}
                    </div>
                    <div className="flex w-full flex-col items-start justify-start gap-4 md:w-2/3">
                        <H1>{user.name}</H1>
                        {isCurrentUser && (
                            <div className="flex w-full flex-col items-center bg-v2-secondary p-[20px]">
                                <div className="flex w-full flex-col md:mb-2 md:flex-row md:justify-between">
                                    <div className="mb-2 flex w-full flex-row items-center break-all text-sm md:mb-0 md:text-base">
                                        {user.phone &&
                                        user.phone.trim() !== "" &&
                                        user.countryCode &&
                                        user.countryCode !== ""
                                            ? `+${
                                                  user.countryCode
                                              } ${formatPhoneNumber(
                                                  user.phone,
                                                  user.countryCode,
                                              )}`
                                            : "No phone number provided"}
                                        {!session?.user
                                            .isUserPhoneNumberVerified && (
                                            <FaEdit
                                                onClick={() =>
                                                    setShowPhoneNumberModal(
                                                        true,
                                                    )
                                                }
                                                className="ml-2 hover:text-secondary"
                                            />
                                        )}
                                    </div>
                                    <div className="w-full break-all text-sm md:text-base">
                                        {user.email}
                                    </div>
                                </div>
                                <div
                                    className={`
                      font-hairline self-start font-mono text-xs italic text-white
                    `}
                                >
                                    Only visible to you
                                </div>
                            </div>
                        )}
                        <div className="flex w-full flex-col gap-3 md:w-2/3">
                            <div className="whitespace-pre-wrap">
                                <SocialLinksComponent
                                    socialLinks={user.socialLinks}
                                />
                                <Linkify
                                    options={{
                                        className: "text-secondary",
                                        target: "_blank",
                                        rel: "noopener noreferrer",
                                    }}
                                >
                                    {user.bio ?? ""}
                                </Linkify>
                            </div>
                        </div>
                        <div className="flex w-full gap-3 md:w-1/2">
                            <div>
                                <H3>BADGES</H3>
                            </div>
                            <div className="flex gap-6 px-4">
                                {user.badges?.map((b, i) => (
                                    <Badge badge={b} key={`${b.id}-${i}`} />
                                ))}
                            </div>
                        </div>
                        <div className="flex w-full content-center gap-3 md:w-1/2">
                            <div className="flex">
                                <H3 className="self-center">MEMBERSHIPS</H3>
                            </div>
                            <div className="flex gap-6 px-4">
                                {user.memberships?.map((m, i) => (
                                    <Chip
                                        key={i}
                                        href={`/${m.community.slug}`}
                                        className="mt-0"
                                    >
                                        {m.community.name}
                                    </Chip>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <PhoneNumberModal
                    showModal={showPhoneNumberModal}
                    mode={user?.countryCode && user?.phone ? "edit" : "add"}
                    setShowModal={setShowPhoneNumberModal}
                />
                {showEditProfileModal && (
                    <EditProfileModal
                        isVisible={showEditProfileModal}
                        setVisible={(e) => {
                            setShowEditProfileModal(e);
                            apiCtx.user.getProfile.invalidate();
                            apiCtx.user.getProfile.refetch();
                            router.refresh();
                        }}
                    />
                )}
            </>
        );
};

export default ProfileCard;
